import ExternalMessageService from './ExternalMessageService';
import MessageService from './MessageService';

const STORAGE_KEY = 'safeguardpro_points_v1';

const readEntries = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeEntries = (entries) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
};

const normalizeEntry = (entry) => ({
  id: entry.id || `pts-${Math.random().toString(36).slice(2, 10)}`,
  studentId: entry.studentId,
  studentName: entry.studentName || 'Student',
  points: Number(entry.points || 0),
  reason: entry.reason || 'Points awarded',
  source: entry.source || 'manual',
  createdBy: entry.createdBy || 'System',
  createdAt: entry.createdAt || new Date().toISOString(),
  metadata: entry.metadata || {}
});

const buildRewardNotification = ({ student, reward, points }) => ({
  subject: `Reward unlocked: ${reward}`,
  body: `${student.name} has reached ${points} points and unlocked: ${reward}.`
});

export default class PointsService {
  list() {
    return readEntries().map((entry) => normalizeEntry(entry));
  }

  listForStudent(studentId) {
    return this.list().filter((entry) => entry.studentId === studentId);
  }

  getTotalForStudent(studentId) {
    return this.listForStudent(studentId).reduce((sum, entry) => sum + (Number(entry.points) || 0), 0);
  }

  awardPoints({ student, points, reason, source = 'manual', createdBy = 'System', thresholds = [], staffRecipients = [], notify = true }) {
    if (!student || !points) return null;
    const list = this.list();
    const prevTotal = list
      .filter((entry) => entry.studentId === student.id)
      .reduce((sum, entry) => sum + (Number(entry.points) || 0), 0);
    const entry = normalizeEntry({
      studentId: student.id,
      studentName: student.name,
      points,
      reason,
      source,
      createdBy
    });
    const next = [entry, ...list];
    writeEntries(next);

    if (notify && thresholds.length) {
      const sorted = thresholds
        .filter((threshold) => threshold && Number(threshold.points) > 0)
        .sort((a, b) => Number(a.points) - Number(b.points));
      const newTotal = prevTotal + Number(points);
      const crossed = sorted.filter((threshold) => prevTotal < Number(threshold.points) && newTotal >= Number(threshold.points));
      if (crossed.length > 0) {
        const messageService = new MessageService();
        const externalService = new ExternalMessageService();
        crossed.forEach((threshold) => {
          const rewardLabel = threshold.reward || 'Reward';
          const notification = buildRewardNotification({
            student,
            reward: rewardLabel,
            points: threshold.points
          });
          if (staffRecipients.length > 0) {
            messageService.send({
              subject: notification.subject,
              body: notification.body,
              fromUser: { id: 'system', name: 'SafeguardPro' },
              recipients: staffRecipients,
              references: [{ type: 'reward', id: student.id, label: `${rewardLabel} (${threshold.points} pts)` }]
            });
          }
          externalService.sendFromStaff({
            subject: `You unlocked a reward: ${rewardLabel}`,
            body: `Great job! You reached ${threshold.points} points and unlocked: ${rewardLabel}.`,
            fromUser: { id: 'system', name: 'SafeguardPro' },
            recipients: [{ id: student.id, name: student.name, type: 'student' }],
            category: 'rewards'
          });
        });
      }
    }

    return entry;
  }

  buildLeaderboard({ students = [], mode = 'overall', referenceStudent = null } = {}) {
    const entries = this.list();
    let eligible = students;
    if (mode === 'class' && referenceStudent) {
      const programmeNames = new Set((referenceStudent.enrolments || []).map((enrol) => enrol.programme));
      eligible = students.filter((student) =>
        (student.enrolments || []).some((enrol) => programmeNames.has(enrol.programme))
      );
    }
    return eligible
      .map((student) => ({
        student,
        total: entries
          .filter((entry) => entry.studentId === student.id)
          .reduce((sum, entry) => sum + (Number(entry.points) || 0), 0)
      }))
      .sort((a, b) => b.total - a.total);
  }
}
